import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MailQuestion, ChevronLeft, Loader as LoaderIcon, MailCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Logo } from "@/components/layout/PublicLayout";

export default function ForgotPassword() {
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || t("Anfrage fehlgeschlagen. Bitte sp\u00e4ter erneut versuchen.", "Request failed. Please try again later."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen dark-section noise-overlay flex items-center justify-center px-4">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="w-full max-w-md">
        <div className="flex justify-center">
          <Logo dark />
        </div>
        <Card className="mt-8 rounded-[20px] border-white/10 bg-white/[0.06] backdrop-blur-md">
          <CardContent className="p-8">
            {!sent ? (
              <>
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-[12px] bg-[#FF3C20]/12 flex items-center justify-center shrink-0">
                    <MailQuestion className="h-5 w-5 text-[#FF3C20]" />
                  </div>
                  <div>
                    <h1 className="font-display text-lg font-semibold text-white">{t("Passwort vergessen", "Forgot password")}</h1>
                    <p className="text-xs text-white/50">
                      {t("Wir senden Ihnen einen Link zum Zur\u00fccksetzen per E-Mail.", "We'll email you a link to reset it.")}
                    </p>
                  </div>
                </div>
                <form onSubmit={submit} className="mt-7 space-y-4">
                  <div className="space-y-1.5">
                    <Label className="text-white/80">{t("Ihre E-Mail-Adresse", "Your email address")}</Label>
                    <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                      placeholder="name@eleven-familyoffice.ch"
                      className="rounded-[10px] bg-white/5 border-white/15 text-white placeholder:text-white/30"
                      data-testid="forgot-pw-email-input" />
                  </div>
                  {error && <p className="text-sm text-red-400" data-testid="forgot-pw-error">{error}</p>}
                  <Button type="submit" disabled={busy} className="w-full rounded-[12px] bg-[#FF3C20] text-white hover:bg-[#E8241B] active:scale-[0.98]" data-testid="forgot-pw-submit">
                    {busy ? <LoaderIcon className="h-4 w-4 animate-spin" /> : t("Link anfordern", "Request link")}
                  </Button>
                </form>
              </>
            ) : (
              <div className="text-center space-y-4" data-testid="forgot-pw-success">
                <div className="mx-auto h-12 w-12 rounded-full bg-[#FF3C20]/12 flex items-center justify-center">
                  <MailCheck className="h-6 w-6 text-[#FF3C20]" />
                </div>
                <h1 className="font-display text-lg font-semibold text-white">{t("E-Mail unterwegs", "Email on its way")}</h1>
                <p className="text-sm text-white/60">
                  {t("Falls ein Konto mit dieser E-Mail existiert, haben wir einen Link zum Zur\u00fccksetzen gesendet. Der Link ist 60 Minuten g\u00fcltig \u2013 pr\u00fcfen Sie auch den Spam-Ordner.",
                     "If an account with this email exists, we've sent a reset link. The link is valid for 60 minutes \u2013 also check your spam folder.")}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
        <div className="mt-6 text-center">
          <Link to="/login" className="inline-flex items-center gap-1 text-sm text-white/50 hover:text-white transition-colors" data-testid="forgot-pw-back-link">
            <ChevronLeft className="h-4 w-4" /> {t("Zur\u00fcck zur Anmeldung", "Back to sign in")}
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
